/**
 * Transfer eligibility - display derivation layer.
 *
 * Thresholds are tenant config on the server. This module maps the
 * violations from `POST /v1/transfers/eligibility-preflight/` onto the
 * `EligibilityState` vocabulary consumed by the wizard.
 */

import type {
  EligibilityPreflightResult,
  EligibilityViolation,
} from '@/admin/hooks/use-transfers'

export type EligibilityState =
  | 'eligible' //  preflight passed OR WARN-only (non-blocking)
  | 'override' //  at least one BLOCK - Warranty Champion can confirm override
  | 'transfer_used' // holding has already exercised its transfer
  | 'no_closing_date' // user hasn't entered a closing date yet
  | 'unknown' //  preflight hasn't loaded yet

export interface EligibilityInput {
  closingDate: string | null
  preflight: EligibilityPreflightResult | null
  preflightLoading?: boolean
  transferUsed?: boolean
}

export interface EligibilityResult {
  state: EligibilityState
  daysSinceClosing: number | null
  label: string
  helperText: string
  /**
   * The BLOCK-severity violations that triggered the ``override``
   * state. Passed to the modal so the user sees the exact rule
   * message the server surfaced (no client-side copy of the rule).
   */
  blockingViolations: EligibilityViolation[]
  /** WARN-severity violations - inline hint, doesn't block. */
  warnings: EligibilityViolation[]
}

function daysSince(closingDate: string | null): number | null {
  if (!closingDate) return null
  const closing = new Date(closingDate)
  const now = new Date()
  const msPerDay = 1000 * 60 * 60 * 24
  const closingMidnight = Date.UTC(
    closing.getUTCFullYear(),
    closing.getUTCMonth(),
    closing.getUTCDate(),
  )
  const nowMidnight = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  )
  return Math.floor((nowMidnight - closingMidnight) / msPerDay)
}

export function computeEligibility({
  closingDate,
  preflight,
  preflightLoading = false,
  transferUsed = false,
}: EligibilityInput): EligibilityResult {
  const days = daysSince(closingDate)

  if (transferUsed) {
    return {
      state: 'transfer_used',
      daysSinceClosing: days,
      label: 'Transfer Used',
      helperText:
        'This property has already exercised its one-time transfer right.',
      blockingViolations: [],
      warnings: [],
    }
  }

  if (!closingDate) {
    return {
      state: 'no_closing_date',
      daysSinceClosing: null,
      label: 'Awaiting Closing Date',
      helperText: 'Enter the property closing date to check eligibility.',
      blockingViolations: [],
      warnings: [],
    }
  }

  // No preflight yet - can't confirm eligibility.
  if (!preflight) {
    return {
      state: 'unknown',
      daysSinceClosing: days,
      label: preflightLoading ? 'Checking\u2026' : 'Unchecked',
      helperText: preflightLoading
        ? 'Running eligibility check against the tenant\u2019s rules\u2026'
        : 'Eligibility will be checked when the server responds.',
      blockingViolations: [],
      warnings: [],
    }
  }

  const blocks = preflight.violations.filter((v) => v.severity === 'BLOCK')
  const warnings = preflight.violations.filter((v) => v.severity === 'WARN')

  if (blocks.length > 0) {
    return {
      state: 'override',
      daysSinceClosing: days,
      label: 'Needs Override',
      helperText:
        blocks[0]?.message ??
        'One or more rules blocked this transfer. Override requires Warranty Champion approval.',
      blockingViolations: blocks,
      warnings,
    }
  }

  if (warnings.length > 0) {
    return {
      state: 'eligible',
      daysSinceClosing: days,
      label: 'Eligible (with warnings)',
      helperText:
        warnings[0]?.message ??
        'This transfer passes but one or more rules surfaced warnings.',
      blockingViolations: [],
      warnings,
    }
  }

  return {
    state: 'eligible',
    daysSinceClosing: days,
    label: 'Eligible',
    helperText: 'All tenant-configured eligibility rules passed.',
    blockingViolations: [],
    warnings: [],
  }
}
